/**
 * SessionCard component for displaying session metadata
 * Fixed-height design with truncated ID, copy button, prominent status badge,
 * single-line message truncation, and compact action buttons
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ProjectBadge } from '@/components/projects/ProjectBadge'
import { SessionStatusBadge } from '@/components/sessions/SessionStatusBadge'
import { usePermissions } from '@/hooks/usePermissions'
import { 
  MessageSquare, 
  Trash2, 
  Play, 
  Eye, 
  User, 
  Copy, 
  Check,
  Square,
  Coins,
  Clock
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { RelativeTime } from '@/components/data-display/RelativeTime'
import { useToast } from '@/hooks/use-toast'
import { cn, formatCompactNumber, TOKEN_THRESHOLD } from '@/lib/utils'
import type { SessionMetadata } from '@/lib/types'

/**
 * Get token threshold level for styling
 */
function getTokenThreshold(tokens: number): 'low' | 'medium' | 'high' {
  if (tokens < TOKEN_THRESHOLD.LOW) return 'low'
  if (tokens < TOKEN_THRESHOLD.MEDIUM) return 'medium'
  return 'high'
}

export interface SessionCardProps {
  /** Session metadata to display */
  session: SessionMetadata
  /** Callback when delete button is clicked */
  onDelete?: (sessionId: string) => void
  /** Callback when cancel button is clicked (for background sessions) */
  onCancel?: (sessionId: string) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Truncate session ID to first 7 characters
 */
function truncateId(sessionId: string): string {
  return sessionId.slice(0, 7)
}

/**
 * Card component for displaying a session with fixed-height layout.
 * Features: truncated ID with copy, prominent status badge, single-line message,
 * compact action buttons.
 * 
 * @example
 * ```tsx
 * <SessionCard 
 *   session={session}
 *   onDelete={(id) => deleteSession(id)}
 * />
 * ```
 */
export function SessionCard({
  session,
  onDelete,
  onCancel,
  className
}: SessionCardProps) {
  const [copied, setCopied] = useState(false)
  const { toast } = useToast()

  // Permission checks for session actions
  const { hasPermission } = usePermissions()
  const canStopSession = hasPermission('session:stop')
  const canResumeSession = hasPermission('session:resume')
  const canDeleteSession = hasPermission('session:delete')

  const {
    sessionId,
    createdAt,
    messageCount,
    lastUserMessage,
    isExpiringSoon,
    status,
    mode,
    projectId,
    projectName,
    isProjectArchived,
    createdByName,
    createdByEmail
  } = session

  const isRunning = status === 'Running'
  const isBackgroundRunning = mode === 'Background' && isRunning

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(sessionId)
      setCopied(true)
      toast({
        description: 'Session ID copied to clipboard'
      })
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast({
        variant: 'destructive',
        description: 'Failed to copy session ID'
      })
    }
  }

  // Determine left border accent color based on state — brand-aligned
  const getBorderAccent = () => {
    if (isRunning) return 'border-l-[3px] border-l-primary'
    if (isExpiringSoon) return 'border-l-[3px] border-l-yellow-500'
    if (status === 'Failed') return 'border-l-[3px] border-l-destructive'
    return ''
  }

  return (
    <TooltipProvider>
      <Card 
        className={cn(
          'transition-shadow hover:shadow-md',
          getBorderAccent(),
          className
        )}
        role="article"
        aria-label={`Session ${sessionId}`}
      >
        <CardContent className="p-3">
          {/* Row 1: ID, metadata, and status badge */}
          <div className="flex items-start justify-between gap-3 mb-1.5">
            {/* Left: ID with copy + metadata */}
            <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground min-w-0">
              {/* Truncated ID with copy button */}
              <div className="flex items-center gap-1">
                <span className="font-mono font-medium text-foreground">
                  #{truncateId(sessionId)}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleCopyId}
                      className="p-1 hover:bg-muted rounded transition-colors"
                      aria-label="Copy full session ID"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy full ID</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <span className="text-muted-foreground">•</span>

              {/* Project Badge */}
              {projectName && projectId && (
                <ProjectBadge
                  projectName={projectName}
                  projectId={projectId}
                  isArchived={isProjectArchived}
                  size="sm"
                />
              )}

              {/* Author */}
              {(createdByName || createdByEmail) && (
                <>
                  {projectName && projectId && <span className="text-muted-foreground">•</span>}
                  <span className="flex items-center gap-1" title={createdByEmail || undefined}>
                    <User className="w-3 h-3" />
                    {createdByName || createdByEmail}
                  </span>
                </>
              )}

              <span className="text-muted-foreground">•</span>

              {/* Relative time */}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <RelativeTime date={createdAt} />
              </span>
            </div>

            {/* Right: Status badge (large) */}
            <div className="flex-shrink-0">
              {status && <SessionStatusBadge status={status} size="default" />}
            </div>
          </div>

          {/* Row 2: Message (single line, truncated) */}
          <div className="mb-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm truncate cursor-default">
                  {lastUserMessage || <span className="text-muted-foreground italic">No messages yet</span>}
                </p>
              </TooltipTrigger>
              {lastUserMessage && lastUserMessage.length > 60 && (
                <TooltipContent className="max-w-md">
                  <p className="whitespace-pre-wrap">{lastUserMessage}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          {/* Row 3: Token badge, Message count + Actions */}
          <div className="flex items-center justify-between">
            {/* Left: Token badge + Message count */}
            <div className="flex items-center gap-3 text-muted-foreground text-sm">
              {/* Token badge */}
              {session.totalTokens != null && session.totalTokens > 0 && (
                <div
                  data-testid="session-token-badge"
                  data-threshold={getTokenThreshold(session.totalTokens)}
                  className={cn(
                    'flex items-center gap-1',
                    getTokenThreshold(session.totalTokens) === 'low' && 'text-green-600',
                    getTokenThreshold(session.totalTokens) === 'medium' && 'text-yellow-600',
                    getTokenThreshold(session.totalTokens) === 'high' && 'text-red-600'
                  )}
                >
                  <Coins className="w-4 h-4" />
                  <span>{formatCompactNumber(session.totalTokens)}</span>
                </div>
              )}
              {/* Message count */}
              <div className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                <span>{messageCount}</span>
              </div>
            </div>

            {/* Right: Action buttons */}
            <div className="flex items-center gap-2">
              <Link to={`/sessions/${sessionId}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
              </Link>

              {/* Stop button for background running sessions */}
              {isBackgroundRunning && onCancel && canStopSession && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCancel(sessionId)}
                  className="h-8 px-3"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Stop
                </Button>
              )}

              {/* Resume button - only show when session can be resumed (not running) */}
              {!isRunning && canResumeSession && (
                <Link to={`/orchestrate/${sessionId}`}>
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 px-3"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Resume
                  </Button>
                </Link>
              )}

              {onDelete && canDeleteSession && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(sessionId)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      aria-label="Delete session"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
